import React, { useState, useEffect } from "react";
import { X, Loader2, User as UserIcon, ArrowUpCircle, ArrowDownCircle } from "lucide-react";
import { WalletActionData, WalletForAction } from "../../../types/Wallet";

interface RowActionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (data: WalletActionData) => void;
  isLoading: boolean;
  isDebit?: boolean;
  wallet: WalletForAction | null;
}

export const RowActionModal: React.FC<RowActionModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading,
  isDebit = false,
  wallet,
}) => {
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [amountError, setAmountError] = useState("");

  useEffect(() => {
    if (isOpen) {
      setAmount("");
      setDescription("");
      setAmountError("");
    }
  }, [isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!amount || Number(amount) <= 0) {
      setAmountError("Le montant doit être supérieur à 0");
      return;
    }
    if (!wallet) return;
    onConfirm({
      user_id: wallet.user_id,
      country_id: wallet.country_id,
      amount: Number(amount),
      description: description || undefined,
    });
  };

  if (!isOpen || !wallet) return null;

  const devise = wallet.country?.devise || "";
  const userName = `${wallet.user?.first_name || ""} ${wallet.user?.last_name || ""}`.trim() || "Utilisateur";

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-2xl">
        <div className="flex justify-between items-center mb-5">
          <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            {isDebit ? (
              <>
                <ArrowDownCircle className="w-6 h-6 text-red-600" /> Débiter le wallet
              </>
            ) : (
              <>
                <ArrowUpCircle className="w-6 h-6 text-green-600" /> Recharger le wallet
              </>
            )}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Info utilisateur */}
        <div
          className={`rounded-lg p-3 mb-5 flex items-center gap-3 ${
            isDebit ? "bg-red-50 border border-red-200" : "bg-green-50 border border-green-200"
          }`}
        >
          <div
            className={`w-10 h-10 rounded-full flex items-center justify-center ${
              isDebit ? "bg-red-200" : "bg-green-200"
            }`}
          >
            <UserIcon className={`w-5 h-5 ${isDebit ? "text-red-700" : "text-green-700"}`} />
          </div>
          <div className="flex-1 min-w-0">
            <p className={`text-sm font-semibold ${isDebit ? "text-red-900" : "text-green-900"}`}>{userName}</p>
            <p className={`text-xs truncate ${isDebit ? "text-red-700" : "text-green-700"}`}>{wallet.user?.email}</p>
            <p className={`text-xs ${isDebit ? "text-red-600" : "text-green-600"}`}>
              Solde actuel :{" "}
              <span className="font-bold">
                {wallet.balance.toLocaleString()} {devise}
              </span>
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Montant */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Montant *</label>
            <div className="relative">
              <input
                type="number"
                step="0.01"
                min="0"
                value={amount}
                onChange={(e) => {
                  setAmount(e.target.value);
                  setAmountError("");
                }}
                className={`w-full px-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 ${
                  isDebit ? "focus:ring-red-500" : "focus:ring-green-500"
                } ${devise ? "pr-16" : ""} ${amountError ? "border-red-300" : "border-gray-300"}`}
                placeholder="Ex: 5000"
                disabled={isLoading}
                autoFocus
              />
              {devise && (
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-gray-500">
                  {devise}
                </span>
              )}
            </div>
            {amountError && <p className="mt-1 text-sm text-red-600">{amountError}</p>}
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              placeholder={isDebit ? "Ex: Correction de solde..." : "Ex: Recharge bonus..."}
              disabled={isLoading}
            />
          </div>

          {/* Résumé */}
          {Number(amount) > 0 && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm space-y-1">
              <p className="font-medium text-gray-700">{isDebit ? "📤 Résumé du débit" : "📥 Résumé de la recharge"}</p>
              <p className="text-gray-600">
                <span className="font-medium">Utilisateur :</span> {userName}
              </p>
              <p className="text-gray-600">
                <span className="font-medium">Montant :</span>{" "}
                <span className={`font-bold ${isDebit ? "text-red-600" : "text-green-600"}`}>
                  {Number(amount).toLocaleString()} {devise}
                </span>
              </p>
              {isDebit && (
                <p className="text-gray-600">
                  <span className="font-medium">Solde après :</span>{" "}
                  <span
                    className={`font-bold ${wallet.balance - Number(amount) < 0 ? "text-red-600" : "text-gray-800"}`}
                  >
                    {(wallet.balance - Number(amount)).toLocaleString()} {devise}
                  </span>
                </p>
              )}
            </div>
          )}

          {/* Boutons */}
          <div className="flex space-x-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              disabled={isLoading}
            >
              Annuler
            </button>
            <button
              type="submit"
              className={`flex-1 text-white px-4 py-2.5 rounded-lg transition-colors flex items-center justify-center space-x-2 disabled:opacity-50 font-medium ${
                isDebit ? "bg-red-600 hover:bg-red-700" : "bg-green-600 hover:bg-green-700"
              }`}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>{isDebit ? "Débit..." : "Recharge..."}</span>
                </>
              ) : (
                <>
                  {isDebit ? <ArrowDownCircle className="w-4 h-4" /> : <ArrowUpCircle className="w-4 h-4" />}
                  <span>{isDebit ? "Débiter" : "Recharger"}</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
